/* eslint-disable camelcase */
import { ElementType, useState } from 'react';
import {
  Box,
  Button,
  useDisclosure,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorModeValue,
  Icon,
  Text,
  HStack,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Divider,
  useToast,
} from '@chakra-ui/react';
import {
  FiPlusCircle,
  FiGrid,
  FiPackage,
  FiCheckCircle,
  FiFilter,
  FiRefreshCw,
} from 'react-icons/fi';

import {
  Order_By,
  useDeleteCategoryByPkMutation,
  useGetAllCategoryDishesQuery,
  useInsertCategoryOneMutation,
  useUpdateCategoryByPkMutation,
} from '../utils/__generated__/graphql';
import { CategorizedFoods, ModernCard, PageHeader } from './shared';
import { CustomModal } from './shared/CustomModal';
import { motion } from 'framer-motion';
import { nhost } from 'src/lib/nhost';
import { compressImage } from 'src/utils';
import { CategoryList } from './shared/CategoryList';

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

export const Categories = () => {
  const toast = useToast();
  const {
    data: categoryDishesData,
    refetch,
    loading,
  } = useGetAllCategoryDishesQuery({
    variables: {
      category_order_by: [{ name: Order_By.Asc }],
      dish_order_by: [{ name: Order_By.Asc }],
    },
  });

  const [insertCategory] = useInsertCategoryOneMutation({
    onCompleted: () => {
      refetch();
      toast({
        title: 'Category added successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });
  const [updateCategory] = useUpdateCategoryByPkMutation({ onCompleted: () => refetch() });
  const [deleteCategory] = useDeleteCategoryByPkMutation({ onCompleted: () => refetch() });

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [categoryName, setCategoryName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);

  // Color mode values
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const accentColor = useColorModeValue('brand.500', 'brand.300');

  const totalCategories = categoryDishesData?.category?.length || 0;
  const totalDishes =
    categoryDishesData?.category?.reduce((acc, cat) => acc + (cat?.dishes_aggregate?.aggregate?.count || 0), 0) || 0;

  const handleCategoryNameChange = (value: string) => {
    setCategoryName(value);
  };

  const openEditModal = (id: React.SetStateAction<null>, name: React.SetStateAction<string>) => {
    setSelectedCategoryId(id);
    setCategoryName(name);
    onOpen();
  };

  const handleAddCategory = async () => {
    try {
      await insertCategory({
        variables: {
          object: { name: categoryName },
        },
      });
      onClose();
    } catch (error: any) {
      console.error('Error adding category:', error.message);
    }
  };

  const handleEditCategory = async () => {
    try {
      await updateCategory({
        variables: {
          pk_columns: { id: selectedCategoryId },
          _set: { name: categoryName },
        },
      });
      onClose();
    } catch (error: any) {
      console.error('Error editing category:', error.message);
    }
  };

  const handleDeleteCategory = async (id: any) => {
    try {
      await deleteCategory({
        variables: {
          id,
        },
      });
    } catch (error: any) {
      console.error('Error deleting category:', error.message);
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    categoryId: string,
    previousFileId?: string
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      if (previousFileId) {
        await nhost.storage.delete({ fileId: previousFileId });
      }

      const compressedFile = await compressImage(file);
      const result = await nhost.storage.upload({
        file: compressedFile,
        bucketId: 'default',
      });

      if (result) {
        await updateCategory({
          variables: {
            pk_columns: { id: categoryId },
            _set: { image: result.fileMetadata?.id },
          },
        });
        toast({
          title: 'Image Updated',
          status: 'success',
          duration: 2000,
        });
      }
    } catch (error: any) {
      toast({
        title: 'Failed to upload Image',
        description: error?.message,
        status: 'error',
        duration: 3000,
      });
    }
  };

  return (
    <Box maxW="8xl" mx="auto" p={{ base: 3, md: 6 }}>
      <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        {/* Header Section */}
        <PageHeader
          title="Food Categories"
          subtitle="Organize and manage your menu structure efficiently"
          badge={
            <Badge colorScheme="brand" fontSize={{ base: 'xs', md: 'sm' }} borderRadius="full" px={3} py={1}>
              {totalCategories}
            </Badge>
          }
          actions={
            <HStack spacing={{ base: 2, md: 4 }}>
              <Tooltip label="Refresh data">
                <IconButton
                  aria-label="Refresh"
                  icon={<Icon as={FiRefreshCw as ElementType} />}
                  isLoading={loading}
                  onClick={() => refetch()}
                  variant="ghost"
                  size={{ base: 'sm', md: 'md' }}
                />
              </Tooltip>
              <Button
                leftIcon={<Icon as={FiPlusCircle as ElementType} />}
                colorScheme="brand"
                size={{ base: 'sm', md: 'md' }}
                onClick={() => openEditModal(null, '')}
                boxShadow="sm"
                _hover={{ transform: 'translateY(-1px)', boxShadow: 'md' }}
                px={{ base: 3, md: 4 }}
              >
                <Text display={{ base: 'none', sm: 'block' }}>Add Category</Text>
                <Text display={{ base: 'block', sm: 'none' }}>Add</Text>
              </Button>
            </HStack>
          }
        />

        {/* Stats Section */}
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 4, md: 6 }} mb={{ base: 6, md: 8 }}>
          <MotionFlex
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ModernCard hover p={{ base: 4, md: 6 }} w="full">
              <Stat>
                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
                  Total Categories
                </StatLabel>
                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={accentColor}>
                  {totalCategories}
                </StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <HStack spacing={2}>
                    <Icon as={FiGrid as ElementType} boxSize={{ base: 3, md: 4 }} />
                    <Text>Active menu categories</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </ModernCard>
          </MotionFlex>

          <MotionFlex
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ModernCard hover p={{ base: 4, md: 6 }} w="full">
              <Stat>
                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
                  Total Dishes
                </StatLabel>
                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={accentColor}>
                  {totalDishes}
                </StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <HStack spacing={2}>
                    <Icon as={FiPackage as ElementType} boxSize={{ base: 3, md: 4 }} />
                    <Text>Available food items</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </ModernCard>
          </MotionFlex>

          <MotionFlex
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ModernCard hover p={{ base: 4, md: 6 }} w="full">
              <Stat>
                <StatLabel fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium" color={textColor}>
                  Average Items
                </StatLabel>
                <StatNumber fontSize={{ base: '2xl', md: '3xl' }} fontWeight="bold" color={accentColor}>
                  {totalCategories ? (totalDishes / totalCategories).toFixed(1) : 0}
                </StatNumber>
                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                  <HStack spacing={2}>
                    <Icon as={FiCheckCircle as ElementType} boxSize={{ base: 3, md: 4 }} />
                    <Text>Dishes per category</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            </ModernCard>
          </MotionFlex>
        </SimpleGrid>

        {/* Main Content */}
        <ModernCard overflow="hidden">
          <Tabs variant="soft-rounded" colorScheme="brand" p={{ base: 4, md: 6 }}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'stretch', md: 'center' }}
              mb={{ base: 4, md: 6 }}
              gap={4}
            >
              <TabList>
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiGrid as ElementType} boxSize={{ base: 4, md: 5 }} />
                    <Text fontSize={{ base: 'sm', md: 'md' }}>Categories</Text>
                  </HStack>
                </Tab>
                <Tab>
                  <HStack spacing={2}>
                    <Icon as={FiPackage as ElementType} boxSize={{ base: 4, md: 5 }} />
                    <Text fontSize={{ base: 'sm', md: 'md' }}>Foods</Text>
                  </HStack>
                </Tab>
              </TabList>

              <HStack>
                <Menu>
                  <MenuButton
                    as={Button}
                    leftIcon={<Icon as={FiFilter as ElementType} />}
                    variant="outline"
                    size={{ base: 'sm', md: 'md' }}
                  >
                    <Text display={{ base: 'none', sm: 'block' }}>Filter</Text>
                    <Text display={{ base: 'block', sm: 'none' }}>Filter</Text>
                  </MenuButton>
                  <MenuList>
                    <MenuItem>Sort by Name</MenuItem>
                    <MenuItem>Sort by Items Count</MenuItem>
                    <Divider />
                    <MenuItem>Show Empty Categories</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
            </Flex>

            <TabPanels>
              <TabPanel px={0}>
                <CategoryList
                  categoryDishesData={categoryDishesData}
                  openEditModal={openEditModal}
                  handleDeleteCategory={handleDeleteCategory}
                  handleFileChange={handleFileChange}
                />
              </TabPanel>
              <TabPanel px={0}>
                <CategorizedFoods dishesData={categoryDishesData} refetch={refetch} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModernCard>
      </MotionBox>

      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        selectedId={selectedCategoryId}
        name={categoryName}
        handleEdit={handleEditCategory}
        handleAdd={handleAddCategory}
        handleNameChange={handleCategoryNameChange}
      />
    </Box>
  );
};
